import { CaughtException } from '../../common'
import { ExtrasStatus } from './channel'
import { CtwaDefaultRetargetingSchedule } from './ctwa'
import { CtwaChannelTemplatesResponse } from './templates'

export type ExtrasRequest = {
    account: string
    channel: string
    draftExtras?: DraftExtras
}

export type ExtrasResponse = ResponseExtras

export type SpendLimitPeriod = 'WEEKLY' | 'MONTHLY'

export type SpendLimitNotifyAt = {
    fifty: boolean
    ninety: boolean
    hundred: boolean
}

/**
 * Os marcos de aviso, do maior para o menor. A ordem importa: o disparo manda apenas o maior limiar
 * cruzado que esteja marcado, e percorrer a lista nesta ordem torna o primeiro casamento a resposta.
 */
export const SPEND_LIMIT_THRESHOLDS = [100, 90, 50] as const

export type SpendLimitThreshold = typeof SPEND_LIMIT_THRESHOLDS[number]

/** Traduz o marco numerico para a chave correspondente em `notifyAt`. */
export const notifyAtKeyOf = (threshold: SpendLimitThreshold): keyof SpendLimitNotifyAt =>
    (({ 50: 'fifty', 90: 'ninety', 100: 'hundred' } as const)[threshold])

/**
 * Controle de gasto do canal com mensagens de sessão.
 *
 * `limitInCents` é inteiro em centavos, seguindo a convenção do messagePricing — R$ 15.000,00 = 1500000.
 * A conversão para reais acontece na borda (tela), nunca no armazenamento.
 */
export type ChannelSpendLimit = {
    enabled: boolean
    limitInCents: number
    period: SpendLimitPeriod
    notifyAt: SpendLimitNotifyAt
    blockOnExceed: boolean
    notificationPhones: string[]
}

/** Consumo do período corrente, apurado na leitura — nunca armazenado. */
export type SpendLimitPeriodUsage = {
    periodKey: string // '2026-09' (mês) ou '2026-W38' (semana ISO, segunda a domingo)
    count: number
    spentInCents: number
}

/**
 * Ausente ou nulo significa "não foi possível apurar" — nunca zero, que mentiria sobre um consumo
 * que pode estar perto do limite.
 */
export type SpendLimitUsage = {
    priceInCents: number
    month: SpendLimitPeriodUsage
    week: SpendLimitPeriodUsage
}

export type SpecificFlow = {
    event?: EventType
    origin?: string
    media?: string
}

export type Extras = {
    useGoBack?: boolean
    useSoundNotification?: boolean
    requestContactInfoEnabled?: boolean
    eventGroupsWorked?: string[]
    ignoredWords?: string[]
    specificFlow?: SpecificFlow
    roomConfiguration?: RoomConfiguration
    configMediaAndOrigin?: ConfiguredTicketAddOn
    ctwaDefaultRetargetingSchedule?: CtwaDefaultRetargetingSchedule
    spendLimit?: ChannelSpendLimit
    configured?: ConfiguredExtras
}

export type ExtrasEventOption = {
    group: string
    types?: {
        typeId: string
        description?: string
    }[]
}

export type ExtrasMediaOption = {
    id: string
    description: string
}

export type ExtrasOriginOption = {
    id: string
    description: string
}

export type EventType = { groupId: string; typeId: string; description?: string }

export type OptionsExtras = {
    event: ExtrasEventOption[]
    media: ExtrasMediaOption[]
    origin: ExtrasOriginOption[]
    templates?: CtwaChannelTemplatesResponse[]
}

export type GenericStatusType = {
    name: string
    error: boolean
}

export type ExtraEventType = {
    group: GenericStatusType
    type: GenericStatusType
}

export type ConfiguredSpecificFlow = {
    event?: ExtraEventType
    media?: GenericStatusType
    origin?: GenericStatusType
}

export type ConfiguredTicketAddOn = {
    account?: string
    type?: string
    context?: string
    key?: string
    value?: string
    channel?: string
    media?: {
        value: string
    }
    origin?: {
        value: string
    }
}

export type ConfiguredExtras = {
    useGoBack?: boolean
    useSoundNotification?: boolean
    requestContactInfoEnabled?: boolean
    ignoredWords?: string[]
    eventGroupsWorked?: string[]
    specificFlow?: ConfiguredSpecificFlow
    roomConfiguration?: RoomConfiguration
    configMediaAndOrigin?: ConfiguredTicketAddOn
    ctwaDefaultRetargetingSchedule?: CtwaDefaultRetargetingSchedule
    spendLimit?: ChannelSpendLimit
    spendLimitUsage?: SpendLimitUsage | null
}

export type RoomConfiguration = {
    usesDefaultTime: boolean
    customTimeRoom: number
}
export type ResponseExtras = {
    configured: ConfiguredExtras
    options: OptionsExtras
    status: ExtrasStatus
}

export type SaveExtrasRequest = {
    channel: string
    extras: Extras
}

export type PublishExtrasRequest = {
    account: string
    channel: string
    editedBy: string
    extras: DraftExtras
}

export type DeleteExtraRequest = {
    group?: string
    channel?: string
    words?: string[]
    eventGroup?: string[]
}

export type SaveExtrasResponse = { duration: number } & CaughtException

export type DraftExtras = { save?: Extras; remove?: DeleteExtraRequest }

export type RemoveExtrasRequest = {
    account: string
    channel: string
}
